import type { CompanyExportField, ExportDataType, ProfileExportField } from '@shared/schemas';
import { all, call, delay, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { FilterGroup } from '@/types/filters';
import { classifyInfraError } from '@/utils/infra-error';

import {
  buildExportRequest,
  type DeactivatedExportScope,
} from '../domain/build-export-request';
import type { ExportFormat } from '../domain/export-plan.model';
import {
  CSV_DOWNLOAD_DELAY_MS,
  planDownloads,
  type PlannedDownload,
} from '../domain/plan-downloads';
import type { ProfileExportGateway } from '../infrastructure/profile-export.gateway';
import { triggerDownload } from '../infrastructure/trigger-download';
import { exportRequested } from './profile-export.actions';
import {
  selectBaseName,
  selectCompanyColumns,
  selectDataTypes,
  selectDeactivated,
  selectFilters,
  selectFormat,
  selectLicenceId,
  selectListId,
  selectProfileColumns,
} from './profile-export.selectors';
import { profileExportActions } from './profile-export.slice';

/**
 * Profile-export saga. All I/O goes through the injected `ProfileExportGateway`
 * (read from the middleware context, never the api client directly), so tests
 * drive every branch with the in-memory gateway. The domain plans the
 * downloads (csv+both = two files); the saga executes the plan: fetch, trigger
 * the browser download, and pause 300 ms between the two CSV files so the
 * browser doesn't merge them.
 */

/** WORKER: build the request(s) from the state, fetch and download the file(s). */
function* exportWorker() {
  const gateway: ProfileExportGateway = yield getContext('profileExportGateway');

  const filters: FilterGroup = yield select(selectFilters);
  const format: ExportFormat = yield select(selectFormat);
  const listId: string | null = yield select(selectListId);
  const deactivated: DeactivatedExportScope | null = yield select(selectDeactivated);
  const licenceId: string | null = yield select(selectLicenceId);
  const dataTypes: ExportDataType[] = yield select(selectDataTypes);
  const profileColumns: ProfileExportField[] = yield select(selectProfileColumns);
  const companyColumns: CompanyExportField[] = yield select(selectCompanyColumns);
  const baseName: string = yield select(selectBaseName);

  yield put(profileExportActions.exportStarted());
  try {
    const downloads: PlannedDownload[] = planDownloads({ format, dataTypes, baseName });
    for (let i = 0; i < downloads.length; i += 1) {
      if (i > 0) yield delay(CSV_DOWNLOAD_DELAY_MS);
      const request = buildExportRequest({
        filters,
        format,
        listId,
        dataTypes,
        profileColumns,
        companyColumns,
        dataType: downloads[i].data_type,
        deactivated,
        licenceId,
      });
      const blob = (yield call(gateway.exportBlob, request)) as Blob;
      yield call(triggerDownload, blob, downloads[i].filename);
    }
    yield put(profileExportActions.exportSucceeded());
  } catch (e) {
    yield put(profileExportActions.exportFailed(classifyInfraError(e, 'Export failed')));
  }
}

/** WATCHERS. */
export function* profileExportSaga() {
  yield all([takeLatest(exportRequested.type, exportWorker)]);
}
